import type { ApplicationStatus, ResumeDocument, ResumeLinterReport } from '@careeropt/schema';
export type { ApplicationStatus, ResumeDocument, ResumeLinterReport } from '@careeropt/schema';

import { reportError } from './errorLog';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** The browser's own IANA timezone — the user's device clock, the most truthful
 *  "local". Sent on every request so the server grounds "now" in the user's zone
 *  (testers in US Eastern, owner in AU) rather than guessing from edge geo. */
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // /auth/* lives at the worker root (browser-navigated for OAuth). API_BASE
  // applies to everything else — same-origin requests go to /api/* on the
  // worker, which handles them under the /api prefix.
  const fullPath = path.startsWith('/auth/') ? path : `${API_BASE}${path}`;
  const action = `${init?.method ?? 'GET'} ${path}`;
  const page = typeof location !== 'undefined' ? location.pathname : '';
  const tz = browserTimeZone();
  let response: Response;
  try {
    response = await fetch(fullPath, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(tz ? { 'X-Time-Zone': tz } : {}),
        ...(init?.headers ?? {}),
      },
      ...init,
    });
  } catch (err) {
    reportError({
      severity: 'error',
      message: err instanceof Error ? err.message : 'network error',
      context: { page, action, status: 0 },
    });
    throw err;
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'unknown' }));
    // Prefer the human-readable `message` the server attached (e.g. "API
    // credits exhausted — top up at …") over the short error code; the code
    // stays available on ApiError.body.error for programmatic handling.
    const message =
      typeof body === 'object' && body && 'message' in body && typeof body.message === 'string'
        ? body.message
        : typeof body === 'object' && body && 'error' in body && typeof body.error === 'string'
          ? body.error
          : response.statusText;
    reportError({ severity: 'error', message, context: { page, action, status: response.status } });
    throw new ApiError(response.status, message, body);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// --- corpus types ------------------------------------------------------------

export interface RoleRecord {
  id: string;
  userId: string;
  company: string;
  title: string;
  location: string | null;
  employerDescriptor: string | null;
  startDate: string;
  endDate: string | null;
  isCurrent: number;
  summary: string | null;
  sortOrder: number;
  metadata: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface AchievementRecord {
  id: string;
  userId: string;
  roleId: string | null;
  /** The sub-event this card is homed to within its role (the `role_events`
   *  child link), when the role carries sub-events (e.g. the one FIFA employment
   *  that delivered both the Club World Cup 2025 and the World Cup 2026). null
   *  when the card sits directly on the role with no sub-event split. Additive +
   *  optional: served by the corrected corpus read once the backend populates it,
   *  and the field the correction-loop re-home moves. */
  roleEventId?: string | null;
  headline: string;
  situation: string | null;
  task: string | null;
  action: string | null;
  result: string | null;
  learning: string | null;
  kpiLabel: string | null;
  kpiValue: string | null;
  tags: string[] | null;
  voiceNoteR2Key: string | null;
  embedding: unknown;
  sortOrder: number;
  /** Rewrite-loop soft state: 'active' | 'merged'. Merged cards render greyed
   *  (processed) and are excluded from dup review + the distilled derive. */
  status: string;
  mergedInto: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportResult {
  ok: boolean;
  // Profile / CV import:
  rolesCreated?: number;
  rolesMatched?: number;
  achievementsCreated?: number;
  achievementsSkipped?: number;
  // Cover-letter import:
  kind?: 'cover_letter';
  archiveId?: string;
  themes?: string[];
  voiceSamplesCount?: number;
  whyCompanyCaptured?: boolean;
  // Common:
  tokens?: { input: number; output: number; cached: number; cacheHit: boolean };
  durationMs?: number;
}

export interface QuickAddResult {
  ok: boolean;
  achievement: AchievementRecord;
  tokens?: { input: number; output: number; cached: number; cacheHit: boolean };
  durationMs?: number;
}

export interface RadarAxes {
  technical_depth: number;
  leadership: number;
  domain_fit: number;
  gtm_or_customer: number;
  distributed_systems: number;
  geo_or_visa: number;
}

export interface JobScoreSnapshot {
  fitScore: number;
  reasoning: string;
  strengths: string[];
  gaps: string[];
  radarAxes: RadarAxes;
  sponsorLikelihood: number;
  sponsorReasoning: string;
  durationMs?: number;
  tokens?: { input: number; output: number; cached: number; cacheHit: boolean };
}

export interface JobRecord {
  id: string;
  userId: string;
  canonicalUrl: string;
  source: string;
  company: string;
  title: string;
  location: string | null;
  workMode: 'remote' | 'hybrid' | 'onsite' | null;
  employmentType: string | null;
  seniority: string | null;
  salaryMinAud: number | null;
  salaryMaxAud: number | null;
  description: string | null;
  requirements: string[] | null;
  sponsorshipMentioned: boolean | null;
  selectionCriteriaMode: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Row shape for the inbox list endpoint (GET /jobs) — joined with the latest
 * score. UNLIKE JobRecord (which mirrors Drizzle's typed, auto-camelCased
 * reads, e.g. GET /jobs/:id), this endpoint reads via a raw
 * `db.execute(sql\`SELECT j.*, ...\`)` — postgres.js does NOT camelCase raw-SQL
 * rows, so every jobs-table column below is genuinely snake_case on the wire
 * (see apps/api/lib/asOf.ts's withAsOf docstring: "this codebase's j.* raw-SQL
 * reads are NOT camelCased before reaching the response"). Deliberately NOT
 * `extends JobRecord` — that shape is wrong for this endpoint. `asOf` /
 * `staleMinutes` are the two fields withAsOf layers on top, and those ARE
 * camelCase (added by application code, not read off the row).
 */
export interface JobListItem {
  id: string;
  user_id: string;
  canonical_url: string;
  source: string;
  company: string;
  title: string;
  location: string | null;
  work_mode: 'remote' | 'hybrid' | 'onsite' | null;
  employment_type: string | null;
  seniority: string | null;
  salary_min_aud: number | null;
  salary_max_aud: number | null;
  description: string | null;
  requirements: string[] | null;
  sponsorship_mentioned: boolean | null;
  selection_criteria_mode: boolean;
  bucket: string | null;
  discovered_at: unknown;
  closed_at: unknown;
  raw: unknown;
  fetched_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined from the latest job_scores row for the viewed scenario (LEFT JOIN
  // LATERAL — null on every field when the job has never been scored under it).
  fit_score: string | null;
  sponsor_likelihood: string | null;
  reasoning: string | null;
  gaps: string[] | null;
  strengths: string[] | null;
  radar_axes: RadarAxes | null;
  scenario_id: string | null;
  // Layered on by withAsOf (apps/api/lib/asOf.ts) — the only two camelCase
  // fields on this row, since they're derived/added, not read off the raw row.
  asOf: string | null;
  staleMinutes: number | null;
}

// --- visa-eligibility verdict (read-only A1 surface) -------------------------
// Client mirror of apps/api LayeredWorkEligibility / EvaluatedPathway. The verdict
// is a deterministic, SOURCED rule snapshot (no model call) and is for DISPLAY
// only — it never feeds scoring. Kept structurally in step with the API contract.

/** Per-pathway roll-up verdict + the overall headline verdict. */
export type VisaVerdict =
  | 'feasible'
  | 'conditional_waivable'
  | 'hard_no_pr_only'
  | 'hard_no_full'
  | 'needs_input';

/** One blocker that fired against a pathway. */
export interface VisaFiredBlocker {
  code: string;
  blocks: 'full' | 'pr_only';
  waiverAvailable: boolean;
  note?: string;
}

/** One evaluated pathway in the matrix cell (best verdict first). */
export interface VisaPathway {
  visaCode: string;
  visaName: string;
  stream?: string | null;
  pathwayType: string;
  verdict: VisaVerdict;
  sponsorRequired: boolean;
  leadsToPr: boolean;
  firedBlockers: VisaFiredBlocker[];
  missingInputs: string[];
  salaryFloorAmount?: number | null;
  salaryFloorCurrency?: string | null;
  guidance?: string | null;
  provenance: { sourceInstrument: string; lastVerified: string; confidence: string };
}

/** The layered work-eligibility result returned by GET /jobs/:id/eligibility. */
export interface JobEligibility {
  jobCountry: string | null;
  status: 'authorized' | 'needs_sponsorship' | 'blocked' | 'unknown';
  needsSponsorship: boolean | null;
  pathways: VisaPathway[];
  basis: string;
  disclaimer: string;
  confidence: 'coarse' | 'rule_based' | 'rule_based_needs_input';
  verdict: VisaVerdict;
  asOf: string;
}

/** Fetch the read-only visa-eligibility verdict for a job. Derived + never
 *  persisted; surfaces the inert A1 engine without changing any score. */
export async function getJobEligibility(jobId: string): Promise<JobEligibility> {
  const r = await apiFetch<{ eligibility: JobEligibility }>(`/jobs/${jobId}/eligibility`);
  return r.eligibility;
}

// --- jobs metrics read-model (read-only A2 data-table surface) ----------------
// Client mirror of apps/api/lib/jobMetrics. Every row is DERIVED-ON-READ from the
// A1 engine (fit + eligibility + honest sponsorship + location-compat) — no model
// call, no persistence, and it NEVER changes a score. KEEP IN SYNC with the API.

/** The honest sponsorship band. When sponsorship is NOT required, companyPropensity
 *  is omitted (the question is moot). When it IS required (or undetermined), the
 *  per-company propensity prior is not built, so it is ALWAYS 'unknown' — surfaced,
 *  never fabricated. */
export interface SponsorBand {
  sponsorshipRequired: boolean | null;
  companyPropensity?: 'unknown';
}

/** One row of the metrics data-table — a job plus its derived A1 signals. */
export interface JobMetricRow {
  jobId: string;
  company: string;
  title: string;
  location: string | null;
  /** Latest fit under the viewing scenario (0..100), or null when unscored. */
  fitScore: number | null;
  /** The job's derived ISO-3166 alpha-2 country, or null when unresolved. */
  jobCountry: string | null;
  eligibilityStatus: JobEligibility['status'];
  eligibilityVerdict: VisaVerdict;
  sponsorBand: SponsorBand;
  /** Job-country vs active-scenario-country. 'unknown' when either side is null. */
  locationCompat: 'match' | 'mismatch' | 'unknown';
  confidence: JobEligibility['confidence'];
}

/** Per-user roll-up counts (by verdict + by sponsorship), for the table header. */
export interface JobMetricsRollup {
  total: number;
  byStatus: Record<JobEligibility['status'], number>;
  byVerdict: Record<VisaVerdict, number>;
  bySponsorship: { required: number; notRequired: number; unknown: number };
  byLocationCompat: { match: number; mismatch: number; unknown: number };
}

/** The full GET /jobs/metrics payload: the rows, the roll-up, and the resolved
 *  viewing scenario + its country (the lens the location-compat is measured against). */
export interface JobsMetricsResponse {
  rows: JobMetricRow[];
  rollup: JobMetricsRollup;
  scenarioId: string | null;
  scenarioCountry: string | null;
}

/** Fetch the read-only metrics read-model for the user's jobs under a scenario.
 *  Derived live from the A1 engine; never persisted, never touches a score. */
export async function getJobsMetrics(scenarioId?: string | null): Promise<JobsMetricsResponse> {
  const qs = scenarioId ? `?scenario=${encodeURIComponent(scenarioId)}` : '';
  return apiFetch<JobsMetricsResponse>(`/jobs/metrics${qs}`);
}

export type WorkAuthorization =
  | 'au_needs_sponsorship'
  | 'au_partner_visa'
  | 'us_authorized'
  | 'other';

export interface ScenarioRecord {
  id: string;
  userId: string;
  name: string;
  active: boolean;
  isPrimary: boolean;
  locations: string[];
  workAuthorization: WorkAuthorization;
  remoteOk: boolean;
  targetCompanies: string[] | null;
  rubricContext: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** One scenario's score line returned by /jobs/ingest and /jobs/:id/score. */
export interface ScenarioScoreLine {
  scenarioId: string;
  scenarioName: string;
  fitScore: number;
  sponsorLikelihood: number;
  isPrimary: boolean;
}

export interface IngestResult {
  ok: boolean;
  job: JobRecord;
  tier: 'json_ld' | 'css' | 'ai_fallback';
  scores: ScenarioScoreLine[];
  scoreErrors?: { scenarioId: string; message: string }[];
}

export interface IngestInput {
  url: string;
  pasteText?: string;
}

// --- tailoring types ---------------------------------------------------------

/** Mirrors BrevityService.BrevityResult on the API. */
export interface BrevityReport {
  wordCount: number;
  syllableCount: number;
  sentenceCount: number;
  fkGradeLevel: number;
  estimatedReadSeconds: number;
  withinBudget: boolean;
  budget: number;
  budgetGradeMax: number;
  flags: string[];
}

export interface CvBrief {
  summaryLine: string;
  leadAchievements: string[];
  gapTranslations: { gap: string; translation: string }[];
}

export interface AdversarialFindings {
  earnsAttention: boolean;
  openingVerdict: string;
  bullshitFlags: { quote: string; why: string }[];
  cuts: string[];
  oneLiner: string;
}

export interface TailoringResult {
  coverLetter: string;
  cvBrief: CvBrief;
  tonePreset: string;
  brevity: BrevityReport;
  au: { passing: boolean; spellingFixes: number; aiTells: { match: string; reason: string }[] };
  vernacular: { swaps: number; applied: { match: string; replacement: string; reason: string }[] };
  sellShort: { passing: boolean; flags: { match: string; reason: string; suggestion: string }[] };
  voiceCritique: AdversarialFindings | null;
  modelId: string;
  durationMs: number;
  tokens: { input: number; output: number; cached: number; cacheHit: boolean };
}

export interface TailorResponse {
  ok: boolean;
  coverLetterId: string;
  scenarioId: string;
  scenarioName: string;
  tailoring: TailoringResult;
}

export async function tailorJob(jobId: string, scenarioId?: string): Promise<TailorResponse> {
  return apiFetch<TailorResponse>(`/jobs/${jobId}/tailor`, {
    method: 'POST',
    body: JSON.stringify({ scenarioId }),
  });
}

/** A row from the cover_letters table — past version a user can view or refine forward from. */
export interface CoverLetterVersion {
  id: string;
  label: string;
  formatVariant: string;
  body: string;
  wordCount: number | null;
  fkGradeLevel: number | null;
  tonePreset: string | null;
  linterReport: {
    brevity?: BrevityReport;
    au?: { passing: boolean; spellingFixes: number; aiTells: { match: string; reason: string }[] };
    vernacular?: {
      swaps: number;
      applied: { match: string; replacement: string; reason: string }[];
    };
    voiceCritique?: AdversarialFindings | null;
    parentCoverLetterId?: string;
    rationale?: string;
  } | null;
  createdAt: string;
}

export async function listCoverLetterVersions(
  jobId: string,
): Promise<{ versions: CoverLetterVersion[] }> {
  return apiFetch<{ versions: CoverLetterVersion[] }>(`/jobs/${jobId}/cover-letters`);
}

export interface RefineResponse {
  ok: boolean;
  coverLetterId: string;
  parentCoverLetterId: string;
  scenarioId: string;
  scenarioName: string;
  tailoring: TailoringResult;
  rationale: string;
}

export async function refineJob(
  jobId: string,
  args: { instructions: string; basedOnCoverLetterId?: string; scenarioId?: string },
): Promise<RefineResponse> {
  return apiFetch<RefineResponse>(`/jobs/${jobId}/refine`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

// --- gap response ("actually I did this") ------------------------------------

/** Source-agnostic input. Mirrors apps/api/lib/capture.ts. Today only 'text'
 *  is wired from the UI; voice/dialogue sources produce the same shape later. */
export type CaptureSource = 'text' | 'voice' | 'dialogue' | 'paste';
export interface InputCapture {
  text: string;
  source: CaptureSource;
  capturedAt?: string;
  meta?: Record<string, unknown>;
}

export interface ExtractedAchievement {
  headline: string;
  situation: string | null;
  task: string | null;
  action: string | null;
  result: string | null;
  kpiLabel: string | null;
  kpiValue: string | null;
  tags: string[];
}

export interface CorpusAmendment {
  extracted: boolean;
  followUpQuestion: string | null;
  achievement: ExtractedAchievement | null;
  suggestedGapTranslation: string | null;
  confidence: number;
  companyHint: string | null;
}

// --- career DNA excavation ---------------------------------------------------

export interface DnaTurnRecord {
  role: 'assistant' | 'user';
  text: string;
  turnIndex: number;
  at: string;
  audioR2Key?: string;
}
export type DnaPhase = 'origins' | 'timeline' | 'synthesis' | 'complete';
export interface DnaSessionRecord {
  id: string;
  userId: string;
  phase: DnaPhase;
  currentRoleId: string | null;
  transcript: DnaTurnRecord[] | null;
  interviewVoiceId: string | null;
  statesideVoiceId: string | null;
  createdAt: string;
  updatedAt: string;
}
export type DnaFacetType = 'accomplishment' | 'knowledge' | 'lesson' | 'thread' | 'driver';
export interface DnaFacetRecord {
  id: string;
  facetType: DnaFacetType;
  content: string;
  isNormalToMe: number;
  roleId: string | null;
  originLabel: string | null;
  turnIndex: number | null;
  linkedAchievementId: string | null;
  confidence: number | null;
}
export interface DnaEra {
  roleId: string | null;
  label: string;
  company?: string;
  title?: string;
  startDate?: string;
  endDate?: string | null;
}
export interface DnaThreadRecord {
  from: string;
  to: string;
  thread: string;
}
export interface DnaProfileRecord {
  id: string;
  origins: string | null;
  drivers: string | null;
  throughLine: string | null;
  threads: DnaThreadRecord[] | null;
  positioningSpine: string | null;
  version: number;
  createdAt: string;
}
// --- corpus DNA overlay (the woven thread, read by the corpus + timeline) ---
export interface DnaOverlay {
  profile: {
    throughLine: string | null;
    drivers: string | null;
    positioningSpine: string | null;
    threads: DnaThreadRecord[] | null;
  } | null;
  facetsByRole: Record<string, DnaFacetRecord[]>;
}
export async function getCorpusDnaOverlay() {
  return apiFetch<DnaOverlay>('/corpus/dna-overlay');
}

export interface TemporalReconcileProposal {
  kind: 'cap_present' | 'merge_dup';
  company: string;
  reason: string;
  // cap_present
  roleId?: string;
  title?: string;
  startDate?: string | null;
  proposedEnd?: string | null;
  recency?: string | null;
  // merge_dup
  targetId?: string;
  sourceIds?: string[];
  count?: number;
}

export interface TemporalReconcile {
  asOf: string;
  zone: string;
  proposals: TemporalReconcileProposal[];
  signals: unknown[];
}

/** The timeline reasoned against the REAL clock: stale-"Present" caps + dup-company
 *  merges, proposed by the (now wired-up) temporal awareness. Read-only. */
export async function getCorpusTemporalReconcile() {
  return apiFetch<TemporalReconcile>('/corpus/temporal-reconcile');
}

// --- embedded conversation probe (anchored to a corpus item; Track B) ---
export interface ProbeResult {
  distilled: { achievements: AchievementRecord[]; facets: DnaFacetRecord[] };
  nextQuestion: string | null;
  overlay: DnaOverlay;
}
export async function probeCorpus(input: {
  anchorType: 'achievement' | 'role' | 'origins';
  anchorId: string | null;
  capture: InputCapture;
}) {
  return apiFetch<ProbeResult>('/corpus/probe', { method: 'POST', body: JSON.stringify(input) });
}

// --- deep enrichment (gap-targeting; Track D) ---
export interface EnrichResult {
  filled: { field: string; anchorId: string }[];
  facets: DnaFacetRecord[];
  overlay: DnaOverlay;
}
export async function enrichCorpus(input?: { anchorId?: string }) {
  return apiFetch<EnrichResult>('/enrich', { method: 'POST', body: JSON.stringify(input ?? {}) });
}

export interface VoiceCandidate {
  voiceId: string;
  name: string;
  gender: string | null;
  accent: string | null;
  previewUrl: string | null;
  slot: 'interview' | 'stateside' | null;
}
export interface VoicePreferenceRecord {
  id: string;
  slot: string;
  voiceId: string;
  voiceName: string | null;
  previewUrl: string | null;
}
export interface DnaTurnResult {
  nextQuestion: string;
  facets: DnaFacetRecord[];
  goldSpotted: boolean;
  phase: DnaPhase;
  advanceEra: boolean;
}

export async function createDnaSession() {
  return apiFetch<{ session: DnaSessionRecord; firstQuestion: string; eras: DnaEra[] }>(
    '/dna/sessions',
    { method: 'POST', body: '{}' },
  );
}
export async function getCurrentDnaSession() {
  return apiFetch<{
    session: DnaSessionRecord | null;
    facets: DnaFacetRecord[];
    profile: DnaProfileRecord | null;
    eras?: DnaEra[];
  }>('/dna/sessions/current');
}
export async function dnaTurn(sessionId: string, capture: InputCapture) {
  return apiFetch<DnaTurnResult>(`/dna/sessions/${sessionId}/turn`, {
    method: 'POST',
    body: JSON.stringify({ capture }),
  });
}
export async function synthesiseDna(sessionId: string) {
  return apiFetch<{ profile: DnaProfileRecord; lint: unknown }>(
    `/dna/sessions/${sessionId}/synthesise`,
    { method: 'POST', body: '{}' },
  );
}
export async function getDnaProfile() {
  return apiFetch<{ profile: DnaProfileRecord | null }>('/dna/profile');
}
export async function listDnaVoices() {
  return apiFetch<{ voices: VoiceCandidate[] }>('/dna/voices');
}
export async function selectDnaVoice(
  slot: 'interview' | 'stateside',
  voiceId: string,
  voiceName?: string | null,
  previewUrl?: string | null,
) {
  return apiFetch<{ ok: boolean }>('/dna/voices/select', {
    method: 'POST',
    body: JSON.stringify({ slot, voiceId, voiceName, previewUrl }),
  });
}
export async function getSelectedDnaVoices() {
  return apiFetch<{
    interview: VoicePreferenceRecord | null;
    stateside: VoicePreferenceRecord | null;
  }>('/dna/voices/selected');
}

// --- Living Corpus v2 ("The Studio") contracts -------------------------------
// MIRRORS apps/api/services/dna/agentContracts.ts (the canonical source). The
// workspaces do not share a types package yet, so these are duplicated on
// purpose. KEEP IN SYNC with the backend file; do not diverge the shapes.

/** landFacet (agent → client): a facet caught mid-conversation, animated from
 *  the voice dock onto its target card by FacetFlight. */
export interface LandFacetPayload {
  facetId: string;
  facetType: DnaFacetType;
  content: string;
  isNormalToMe: boolean;
  /** Where the chip lands: roleId for a career era; null with originLabel for a
   *  pre-career origin; both null for an unplaceable/global facet. */
  targetRoleId: string | null;
  /** The achievements.id when the facet is a promoted accomplishment, so the
   *  LivingCard in-fills its STAR. null otherwise. */
  targetAchievementId: string | null;
  originLabel: string | null;
  /** The fast-tier distiller's confidence (0-1). OPTIONAL + server-only in intent:
   *  carried for the DO's per-turn dedupe tie-break (enrichment-capture); the FE
   *  does not read it. Mirrored here only to keep the two payload shapes in sync. */
  confidence?: number;
}

/** focusCard (client → agent): fired when the user taps a card mid-session so
 *  the agent pivots to it. */
export interface FocusCardPayload {
  anchorType: 'achievement' | 'role' | 'origins';
  anchorId: string | null;
}

/** rehomeCard (agent → client): the spoken-correction PROPOSAL. Emitted when the
 *  agent hears a re-home ("it's the World Cup, not the Club World Cup"). It is a
 *  PROPOSAL only — the agent NEVER auto-applies; the client surfaces a confirm
 *  chip and the user's explicit yes is what triggers `PUT …/rehome`
 *  (human-in-the-loop, hard rule #1). The payload is everything the chip needs to
 *  reflect the proposed move back to the user, plus the verbatim spoken words for
 *  the gated `source_ref`. Mirrors `LandFacetPayload`'s shape; the FE never
 *  decides the destination era — it shows the proposal and posts the confirm. */
export interface RehomeCardPayload {
  /** The card the correction is about (achievements.id). */
  achievementId: string;
  /** The card's current headline, so the chip can name what is moving without a
   *  refetch ("Move *Onboarding for FIFA* …"). */
  achievementHeadline: string;
  /** The PROPOSED destination era / sub-event (never auto-applied). NESTED to MIRROR
   *  the backend's canonical `RehomeCardPayload.proposedTarget` (agentContracts.ts)
   *  EXACTLY — the two shapes are now structurally identical so a backend proposal
   *  coerces on the client (Fix 3: the earlier flat shape missed the nested fields and
   *  dropped every voice proposal). Exactly one of roleId / roleEventId is set; `label`
   *  is the server-resolved human label the chip renders ("the World Cup 2026"). The
   *  FE renders the label; it never resolves an id. */
  proposedTarget: {
    /** A different employment (roles.id) — a cross-role re-home. */
    roleId: string | null;
    /** A sub-event of the SAME employment (role_events.id) — the FIFA split. */
    roleEventId: string | null;
    /** The destination's spoken label, display + the gate's trace target. */
    label: string;
  };
  /** The user's VERBATIM spoken correction — carried through as the gated
   *  `source_ref` on confirm (NO INVENTION: the destination is the user's words). */
  spokenCorrection: string;
}

/** The client-tool names, frozen. Must match the ElevenLabs agent config AND
 *  the React SDK registration verbatim or tool calls will not route.
 *  `rehomeCard` is the correction-loop voice tool (§3.3): registering it here is
 *  necessary but NOT sufficient — the ElevenLabs agent dashboard must register
 *  the identical `rehomeCard` name or the spoken tool call silently fails to
 *  route (the deploy dependency called out in the build plan). */
export const CLIENT_TOOL_NAMES = {
  landFacet: 'landFacet',
  focusCard: 'focusCard',
  rehomeCard: 'rehomeCard',
} as const;

/** The provenance object stamped on the re-homed (superseding) row. For the voice
 *  path it carries the user's VERBATIM spoken correction; for the drag-drop path
 *  the deliberate drop IS the grounded signal. Mirrors the live write gate's
 *  `source_ref` shape (NO INVENTION: a card can only be re-homed to a place the
 *  user's own words / deliberate act support). The backend re-grounds + stamps
 *  `faithfulness`; the FE only supplies what the user did. */
export interface RehomeSourceRef {
  /** 'drag_drop' — the manual drop; 'spoken_correction' — the voice confirm. */
  kind: 'drag_drop' | 'spoken_correction';
  /** The user's words (voice path) or a short note of the manual act (drag path).
   *  This is the signal the destination gate traces against. */
  capturedFrom: string;
}

/** Body for PUT /corpus/achievements/:id/rehome — the §6 supersede that re-homes
 *  a card to a corrected era. EXACTLY ONE of targetRoleId / targetRoleEventId is
 *  set: a different employment, or a different sub-event under the same
 *  employment (the FIFA split). The backend gates the destination to the
 *  `sourceRef` words, runs the supersede (old row valid_to'd, new row
 *  valid_from'd + supersedes-chained), and re-grounds — the FE posts the drop /
 *  confirm and re-renders from the served corrected read. */
export interface RehomeRequest {
  targetRoleId?: string;
  targetRoleEventId?: string;
  sourceRef: RehomeSourceRef;
}

/** Result of a re-home: the new (superseding) row id + the old (superseded) row
 *  id, so the caller can confirm the chain landed. The FE does not depend on the
 *  shape beyond `ok` — it re-reads the corpus for the corrected truth. */
export interface RehomeResult {
  ok: boolean;
  newId: string;
  supersededId: string;
}

/**
 * Re-home a card to a corrected era via the §6 supersede (the correction loop).
 * Both UXs — the drag-drop re-home and the voice confirm chip — call THIS one
 * function, so the loop is one law everywhere.
 *
 * NOTE (seam): as of this FE build the route `PUT /corpus/achievements/:id/rehome`
 * is owned by the backend/grounding team and may not be live yet. This call is
 * typed to the agreed contract (build plan §3.2); when the route lands it works
 * unchanged. Until then a call surfaces the normal ApiError (404), which the UI
 * shows as the error state — no local era mutation, ever (the served corrected
 * read is the only source of truth).
 * TODO(backend-seam): remove this note once `PUT …/rehome` + CorrectionRehomeService
 * are live (build plan §7 steps 1–2).
 */
export async function rehomeAchievement(id: string, body: RehomeRequest): Promise<RehomeResult> {
  return apiFetch<RehomeResult>(`/corpus/achievements/${id}/rehome`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Confirm a SPOKEN re-home THROUGH the live session (the in-the-moment correction loop).
 * Unlike `rehomeAchievement` (the drag-drop PUT), the voice confirm posts the nested
 * `RehomeCardPayload` the confirm chip already holds to `POST …/rehome-confirm`, which
 * the bridge route forwards to the user's ConversationSession DO. The DO runs the SAME §6
 * supersede + destination gate (the user's VERBATIM words are the gated source_ref, NO
 * INVENTION), bumps the corpus epoch so the next turn re-grounds, and the agent
 * acknowledges the move in-conversation. The body is the chip's payload verbatim; the
 * URL's `:id` is the authoritative card id (the backend never trusts a body id for
 * identity). Returns the same { ok, newId, supersededId } the drag-drop path returns.
 *
 * Drag-drop still uses `rehomeAchievement` (PUT /rehome) — no agent is in a drag. This
 * function is the voice path ONLY (the confirm flows through the agent's own session).
 */
export async function rehomeConfirmViaSession(payload: RehomeCardPayload): Promise<RehomeResult> {
  return apiFetch<RehomeResult>(`/corpus/achievements/${payload.achievementId}/rehome-confirm`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Response from POST /api/agent-session. The client passes conversationToken to
 *  startSession({ conversationToken, connectionType: 'webrtc' }) and applies
 *  overrides + dynamicVariables. The ElevenLabs key never reaches the client. */
export interface AgentSessionResponse {
  sessionId: string;
  /** Echoed back so the client forwards it in customLlmExtraBody (the custom-LLM
   *  endpoint is server-to-server, so this is how it learns whose corpus to load). */
  userId: string;
  conversationToken: string;
  agentId: string;
  overrides: {
    agent?: {
      prompt?: { prompt: string };
      firstMessage?: string;
      language?: string;
    };
    tts?: { voiceId?: string };
  };
  dynamicVariables: Record<string, string | number | boolean>;
}

/** Request body for POST /api/agent-session: which card/role the session opens
 *  on. All optional (an unanchored open session is valid). `gap` points the
 *  trusted-friend session at a live job-application gap (the "talk it through"
 *  entry from the gap analysis) instead of a corpus card. */
export interface AgentSessionRequest {
  anchorType?: 'achievement' | 'role' | 'origins' | 'gap';
  anchorId?: string | null;
  sessionId?: string;
  /** Required when anchorType === 'gap': the scored gap line + the job it sits on
   *  (the server loads the JD company/title from jobId to ground the agent). */
  gap?: { text: string; jobId: string };
}

/** GET /api/dna/health shape: ElevenLabs reachability + voice-drift. */
export interface DnaHealthResponse {
  elevenlabs: 'ok' | 'fail';
  account: string | null;
  voiceCount: number | null;
  voices: { slot: 'interview' | 'stateside'; voiceId: string; resolves: boolean }[];
}

/** Mint a Studio voice-session conversation token + grounding. */
export async function startAgentSession(input: AgentSessionRequest = {}) {
  return apiFetch<AgentSessionResponse>('/agent-session', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Read the ElevenLabs health + voice-drift report. */
export async function getDnaHealth() {
  return apiFetch<DnaHealthResponse>('/dna/health');
}

// --- admin: errors & health dashboard ---------------------------------------
// Guarded server-side by requireAdmin (ADMIN_EMAILS allowlist). The nav item is
// shown only when adminErrorsPing() resolves ok:true.

/** One persisted error_logs row. */
export interface ErrorLogRow {
  id: string;
  userId: string | null;
  source: 'client' | 'server';
  severity: 'error' | 'warn' | 'fatal';
  message: string;
  stack: string | null;
  context: {
    page?: string;
    action?: string;
    status?: number;
    kind?: string;
    [k: string]: unknown;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminErrorsFilters {
  severity?: 'error' | 'warn' | 'fatal';
  source?: 'client' | 'server';
  /** Lookback window in hours when `since` is absent (default 168 = 7d). */
  hours?: number;
  since?: string;
  /** Bring the studio-trace breadcrumbs (kind='trace') back into the view. */
  includeTrace?: boolean;
  limit?: number;
}

export interface AdminErrorsResponse {
  errors: ErrorLogRow[];
  filters: {
    severity: string | null;
    source: string | null;
    since: string;
    includeTrace: boolean;
  };
}

export interface AdminErrorsRollup {
  since: string;
  includeTrace: boolean;
  total: number;
  /** Trace rows excluded from the default view (0 when includeTrace). */
  traceHidden: number;
  bySource: { source: string; count: number }[];
  bySeverity: { severity: string; count: number }[];
  topMessages: { message: string; count: number }[];
  topCodes: { code: string; count: number }[];
  perDay: { day: string; count: number }[];
  /** Folded-in ElevenLabs reachability + voice-drift probe. */
  health: DnaHealthResponse;
}

function adminErrorsQuery(f: AdminErrorsFilters = {}): string {
  const qs = new URLSearchParams();
  if (f.severity) qs.set('severity', f.severity);
  if (f.source) qs.set('source', f.source);
  if (f.since) qs.set('since', f.since);
  if (f.hours != null) qs.set('hours', String(f.hours));
  if (f.includeTrace) qs.set('includeTrace', 'true');
  if (f.limit != null) qs.set('limit', String(f.limit));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/** Cross-user error feed (admin only). */
export async function getAdminErrors(f: AdminErrorsFilters = {}): Promise<AdminErrorsResponse> {
  return apiFetch<AdminErrorsResponse>(`/errors/admin${adminErrorsQuery(f)}`);
}

/** Rollup counts + top message/code + per-day + health (admin only). */
export async function getAdminErrorsRollup(f: AdminErrorsFilters = {}): Promise<AdminErrorsRollup> {
  return apiFetch<AdminErrorsRollup>(`/errors/admin/rollup${adminErrorsQuery(f)}`);
}

/**
 * Admin probe. Resolves true if the caller is an admin, false on a 403 (or any
 * other failure). Used to decide whether to render the admin nav item without
 * touching /auth/me.
 *
 * Uses a RAW fetch, NOT apiFetch, on purpose: apiFetch funnels every non-OK
 * response through reportError, which would persist an expected 403 to
 * error_logs each time a non-admin loads the app — re-polluting the very log
 * this dashboard exists to keep clean. The probe stays silent.
 */
export async function adminErrorsPing(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/errors/admin/ping`, { credentials: 'include' });
    if (!res.ok) return false;
    const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
    return body?.ok === true;
  } catch {
    return false;
  }
}

export interface GapPreviewResponse {
  ok: boolean;
  committed: false;
  amendment: CorpusAmendment;
}
export interface GapCommitResponse {
  ok: boolean;
  committed: true;
  achievementId: string;
  headline: string;
  matchedRole: string | null;
}

/** Preview: extract an achievement from what the user said. Persists nothing. */
export async function previewGapResponse(
  jobId: string,
  gap: string,
  capture: InputCapture,
): Promise<GapPreviewResponse> {
  return apiFetch<GapPreviewResponse>(`/jobs/${jobId}/respond-to-gap`, {
    method: 'POST',
    body: JSON.stringify({ gap, capture }),
  });
}

/** Commit: persist the (possibly user-edited) achievement to the corpus. */
export async function commitGapResponse(
  jobId: string,
  gap: string,
  achievement: ExtractedAchievement,
  companyHint: string | null,
): Promise<GapCommitResponse> {
  return apiFetch<GapCommitResponse>(`/jobs/${jobId}/respond-to-gap`, {
    method: 'POST',
    body: JSON.stringify({
      gap,
      commit: true,
      achievement,
      companyHint,
      capture: { text: '(commit)', source: 'text' },
    }),
  });
}

// --- resume / CV render ------------------------------------------------------

export interface BuildResumeResponse {
  ok: boolean;
  resumeId: string;
  scenarioId: string;
  scenarioName: string;
  document: ResumeDocument;
  linterReport: ResumeLinterReport;
}

export interface ResumeVersion {
  id: string;
  label: string;
  formatVariant: string;
  pages: number | null;
  wordCount: number | null;
  linterReport: ResumeLinterReport | null;
  createdAt: string;
}

export async function buildResume(
  jobId: string,
  args: { scenarioId?: string; chosenAchievementIds?: string[] },
): Promise<BuildResumeResponse> {
  return apiFetch<BuildResumeResponse>(`/jobs/${jobId}/resume`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function listResumeVersions(jobId: string): Promise<{ versions: ResumeVersion[] }> {
  return apiFetch<{ versions: ResumeVersion[] }>(`/jobs/${jobId}/resumes`);
}

export async function getResume(resumeId: string): Promise<{
  id: string;
  jobId: string | null;
  label: string;
  formatVariant: string;
  document: ResumeDocument;
  linterReport: ResumeLinterReport | null;
}> {
  return apiFetch(`/resumes/${resumeId}`);
}

/**
 * The job-FREE corpus résumé: the user's whole career composed AS a CV, with no
 * job to tailor against. The backend (ResumeComposerService, job-free path)
 * synthesises the ordering + profile prose + skills; this is the stable seam the
 * /corpus résumé view renders verbatim through <ResumeView>. The FE never
 * derives any of this shape — composition is the server's job (NO INVENTION).
 *
 * TODO(backend): `GET /api/corpus/resume` is owned by the backend team and may
 * not be live yet. This call is typed to the ResumeDocument contract
 * (packages/schema/src/resume_document.ts) so the FE compiles + degrades
 * gracefully (loading / empty / error) until the route lands. No FE fallback /
 * fabrication — when the route 404s the view shows its honest empty state.
 */
export async function getCorpusResume(): Promise<{ document: ResumeDocument }> {
  return apiFetch<{ document: ResumeDocument }>('/corpus/resume');
}

// --- positioning ("re-tell at full truthful strength") ----------------------

export interface RepositionPreview {
  ok: boolean;
  committed: false;
  original: { headline: string; result: string | null };
  retold: {
    headline: string;
    result: string;
    rationale: string;
    /** Details the engine added that aren't verbatim — vet each. */
    inferences: string[];
    confidence: number;
  };
  sellShort: { passing: boolean; flags: { match: string; reason: string; suggestion: string }[] };
}

/** Preview a repositioned achievement. Persists nothing — the caller accepts
 *  into the edit fields and saves through the normal update flow. */
export async function repositionAchievement(
  id: string,
  targetContext?: string,
): Promise<RepositionPreview> {
  return apiFetch<RepositionPreview>(`/corpus/achievements/${id}/reposition`, {
    method: 'POST',
    body: JSON.stringify({ targetContext }),
  });
}

export interface SimilarPair {
  a: AchievementRecord;
  b: AchievementRecord;
  score: number;
  sameRole: boolean;
}

export interface SimilarPairsResult {
  pairs: SimilarPair[];
  threshold: number;
  total: number;
}

// --- R123: cross-role KPI conflicts (the "$1.8M under two employers" class) ---

export interface ConflictRowView {
  id: string;
  roleId: string | null;
  company: string | null;
  headline: string;
  kpiValue: string | null;
}

export interface ConflictSetView {
  kpiKey: string;
  kpiDisplay: string;
  rows: ConflictRowView[];
  roleCount: number;
  maxCrossRoleSimilarity: number;
}

export interface ConflictsResult {
  conflicts: ConflictSetView[];
  scanned: number;
}

/** Served, read-only detection — resolution is the user's (rehome / archive). */
export function fetchCorpusConflicts(): Promise<ConflictsResult> {
  return apiFetch<ConflictsResult>('/corpus/conflicts');
}

// --- rewrite loop: combine-refine (two dup cards → one superior card) ---

export interface RefinedCardFields {
  headline: string;
  situation?: string | null;
  task?: string | null;
  action?: string | null;
  result?: string | null;
  learning?: string | null;
  kpiLabel?: string | null;
  kpiValue?: string | null;
  tags?: string[];
}

export interface MergeRefinePreview {
  survivorId: string;
  mergedId: string;
  refined: RefinedCardFields;
  /** Uncertainty/contradictions the engine refused to resolve silently. */
  flags: string[];
  lint: {
    au: { passing: boolean; totalIssues: number };
    sellShort: { passing: boolean };
    brevity: { withinBudget: boolean; wordCount: number };
  };
  throughLineUsed: boolean;
}

/** PREVIEW the fused card (no write). Survivor keeps its id on apply. */
export async function previewMergeRefine(survivorId: string, mergedId: string) {
  return apiFetch<MergeRefinePreview>('/corpus/achievements/merge-refine', {
    method: 'POST',
    body: JSON.stringify({ survivorId, mergedId }),
  });
}

/** APPLY the approved fuse: survivor updated, the other card soft-merged. */
export async function applyMergeRefine(
  survivorId: string,
  mergedId: string,
  refined: RefinedCardFields,
) {
  return apiFetch<{ ok: boolean; survivor: AchievementRecord; merged: AchievementRecord }>(
    '/corpus/achievements/merge-refine',
    { method: 'PUT', body: JSON.stringify({ survivorId, mergedId, refined }) },
  );
}

// --- rewrite loop: single-card reframe (one card, rewritten through the through-line) ---

/** The original card as the engine saw it — rides along in the preview so the
 *  UI can show before → after. */
export interface RefineOriginal extends RefinedCardFields {
  id: string;
  roleCompany?: string | null;
  roleTitle?: string | null;
}

export interface RefinePreview {
  cardId: string;
  original: RefineOriginal;
  refined: RefinedCardFields;
  /** Uncertainty/contradictions the engine refused to resolve silently. */
  flags: string[];
  lint: MergeRefinePreview['lint'];
  throughLineUsed: boolean;
}

/** PREVIEW one card rewritten through the through-line (no write). */
export async function previewRefineAchievement(id: string) {
  return apiFetch<RefinePreview>(`/corpus/achievements/${id}/refine`, { method: 'POST' });
}

/** APPLY the user-approved rewrite onto the card. */
export async function applyRefineAchievement(id: string, refined: RefinedCardFields) {
  return apiFetch<{ achievement: AchievementRecord }>(`/corpus/achievements/${id}/refine`, {
    method: 'PUT',
    body: JSON.stringify({ refined }),
  });
}

/** UNMERGE: restore a soft-merged duplicate to active. The survivor keeps its
 *  refined text — this brings the card back, it does not un-edit the survivor. */
export async function unmergeAchievement(id: string) {
  return apiFetch<{ achievement: AchievementRecord }>(`/corpus/achievements/${id}/unmerge`, {
    method: 'PUT',
  });
}

// --- ambient listening track: the second-track backchannel clips ---

export type BackchannelCategory = 'nod' | 'impressed' | 'empathy' | 'keen';

export interface BackchannelClip {
  id: string;
  category: BackchannelCategory;
  text: string;
  /** mp3 bytes, base64 — decoded client-side into a playable blob. */
  audioB64: string;
}

/** The fixed reaction-clip set in the agent's own voice (KV-cached server-side;
 *  cache the response client-side too — it never changes per voice). */
export async function getBackchannelClips() {
  return apiFetch<{ voiceId: string; clips: BackchannelClip[] }>('/backchannel/clips');
}

// --- facet → card promotion: spoken wins become real cards ---

export interface PromotableFacet {
  id: string;
  content: string;
  isNormalToMe: number;
  roleId: string | null;
  company: string | null;
}

/** Accomplishment facets the voice banked that never became cards (gold first). */
export async function getPromotableFacets() {
  return apiFetch<{ facets: PromotableFacet[]; count: number }>('/corpus/promotable-facets');
}

/** Promote a batch (top 8 by gold/recency when ids omitted): each is STAR-
 *  structured and lands as a card on its role; the facet keeps the link. */
export async function promoteFacets(facetIds?: string[]) {
  return apiFetch<{
    promoted: number;
    failed: number;
    achievements: { id: string; headline: string }[];
  }>('/corpus/promote-facets', {
    method: 'POST',
    body: JSON.stringify(facetIds?.length ? { facetIds } : {}),
  });
}

/** Adopt voice-promoted orphan cards onto their roles (content-matched, never
 *  guessed). Fired on corpus load; cheap no-op when there are no orphans. */
export async function adoptOrphanCards() {
  return apiFetch<{ adopted: number; examined: number; unmatched: number }>(
    '/corpus/adopt-orphans',
    { method: 'POST', body: JSON.stringify({}) },
  );
}

// --- presence: role discovery ("Where you fit") ---

export type RoleMatchTier = 'on_target' | 'strong_adjacent' | 'stretch';

export interface RoleMatchItem {
  title: string;
  tier: RoleMatchTier;
  why: string;
  fitSignals: string[];
  stretchGaps: string[];
}

export interface RoleMatchResultData {
  summary: string;
  matches: RoleMatchItem[];
}

/** Discover roles from who you actually are — your named roles + the gleaned
 *  interview DNA + capabilities + gold. Surfaces fits you wouldn't self-select. */
export async function matchRoles(desiredRoles?: string[]) {
  return apiFetch<{ result: RoleMatchResultData }>('/presence/role-match', {
    method: 'POST',
    body: JSON.stringify(desiredRoles?.length ? { desiredRoles } : {}),
  });
}

/** The user's target roles for Get Found, served from the DB (never hardcoded). */
export async function getTargetRoles(): Promise<string[]> {
  const r = await apiFetch<{ roles: string[] }>('/presence/target-roles');
  return r.roles;
}

/** Persist the user's target roles (called on every chip edit — type / promote /
 *  remove). The DB is the source of truth; the UI only forwards the new list. */
export async function saveTargetRoles(roles: string[]): Promise<string[]> {
  const r = await apiFetch<{ roles: string[] }>('/presence/target-roles', {
    method: 'PUT',
    body: JSON.stringify({ roles }),
  });
  return r.roles;
}

// --- presence: the "be found" surface (bridge + platform profiles) ---

/** A presence platform id. Widened to the SERVED set (any lake platform key),
 *  not the legacy 3-literal union — the lake's validated-criteria gate decides
 *  which platforms are scoreable, so the FE holds no hardcoded platform list.
 *  The named literals stay only as editor hints. */
export type PresencePlatform = 'linkedin' | 'seek' | 'glassdoor' | (string & {});

export interface PresenceBridgeItem {
  yourPhrasing: string;
  standardTerm: string;
  evidenceRefId: string;
  claimLevel: string;
  confidence: number;
}

export interface PresenceProfileResult {
  platform: PresencePlatform;
  /** Job titles to be found as / apply for, ranked best-first. */
  recommendedTitles: string[];
  headline: string;
  about: string;
  experienceBlocks?: { role: string; bullets: string[] }[];
  skills: string[];
  /** Server-rendered, PLATFORM-SHAPED copy-paste strings (renderPresencePaste, lake-
   *  driven per platform_format). The FE renders THESE for the copy buttons instead of
   *  re-gluing shaping client-side (the old LinkedIn-shaped-for-everyone bandaid). `full`
   *  is the whole profile as one platform-ordered block (the "copy all"). Optional so an
   *  older API body still types — the FE falls back to its own join when absent. */
  paste?: {
    headline: string;
    about: string;
    experience: string;
    skills: string;
    recommendedTitles: string;
    full: string;
  };
  lint: {
    au: { passing: boolean; totalIssues: number };
    sellShort: { passing: boolean };
    brevity: { withinBudget: boolean; wordCount: number };
  };
}

/** One citation behind a scored criterion — the presentation view of a lake
 *  `LakeSource` (structurally `{ url, title?, retrievedAt? }`). Kept as a thin
 *  FE DTO so this module needs no schema-package import; the API owns the canonical
 *  shape. `url` is the only required field; the rest may be absent. */
export interface PresenceOptimisationSource {
  url: string;
  title?: string;
  retrievedAt?: string;
}

/** One scored criterion in the optimisation readout. Mirrors a row of the
 *  `platform_criteria` lake AS SCORED (scorePlatformOptimisation): the
 *  plain-English explanation is shown up front; `why_it_matters` + `source` +
 *  `last_verified_at` + `confidence` sit behind a "more info" tap. `source` is
 *  the criterion's citation list (full provenance, read behind the tap). */
export interface PresenceOptimisationCriterion {
  key: string;
  field?: string | null;
  label: string;
  /** The explicit, up-front explanation — shown WITHOUT a tap. */
  plain_english: string;
  /** The "machine behind the curtain" line — behind the more-info tap. */
  why_it_matters?: string | null;
  /** Whether the profile satisfies this criterion. */
  met: boolean;
  /** What to do when unmet (the live number already filled in by the scorer). */
  fix_hint?: string | null;
  importance?: 'high' | 'medium' | 'low';
  /** 0-100 source-strength benchmark shown to the user (Jason's certainty). */
  confidence?: number | null;
  /** The criterion's citations (full list, read behind the tap). */
  source?: PresenceOptimisationSource[];
  /** ISO timestamp the criterion was last verified. */
  last_verified_at?: string | null;
  validation_status?: 'validated' | 'flagged' | 'contested' | 'rejected';
  /** How the criterion participates in the readout (the 3-way hybrid): 'scored' counts
   *  in the %, 'guaranteed' is shown ✓ (the system enforces it), 'advisory' is guidance.
   *  Optional → an older API body without it renders as 'scored' (back-compat). */
  display_role?: 'scored' | 'guaranteed' | 'advisory';
}

/** Server-COMPUTED platform-optimisation readout (never client-scored). Owned by
 *  the API (scorePlatformOptimisation), scored ONLY against VALIDATED, sourced
 *  `platform_criteria` for the platform plus the cross-platform universals — the
 *  no-invention gate means an unvalidated/guessed criterion can never enter a
 *  score. Returned as an additive field on /presence/generate; the FE renders it
 *  read-only.
 *
 *  THE THREE HONEST STATES (validation_status):
 *   - `unvalidated`: no validated criteria yet → `score` is **null** (NEVER 0) —
 *     "criteria being researched", no number shown.
 *   - `partial`: some criteria validated (e.g. the universals) → a real score over
 *     `validated_count` of `total_count`.
 *   - `validated`: the full per-criterion readout.
 *
 *  Sub-fields are optional so a degraded/empty/older-API server body still types. */
export interface PresenceOptimisation {
  platform?: string;
  /** Which honest state this readout is in (drives the FE branch). */
  validation_status?: 'validated' | 'partial' | 'unvalidated';
  /** How many criteria were validated (the scored numerator's basis). */
  validated_count?: number;
  /** How many criteria exist for this platform (the honest denominator). */
  total_count?: number;
  /** 0..100 overall optimisation score, or **null** when unvalidated (we never
   *  show a score we cannot stand behind — null is not a bad score, it is "not
   *  yet scoreable"). */
  score?: number | null;
  /** Colour/word band of the score; null when there is no score. */
  band?: 'strong' | 'good' | 'needs_work' | 'thin' | null;
  /** Per-criterion met/unmet breakdown with served copy + citations. */
  criteria?: PresenceOptimisationCriterion[];
  /** The keyword-stuffing check: honest mirroring ranks, stuffing is penalised.
   *  `scored` is false when stuffing could not be assessed (no validated rule). */
  stuffing?: { detected: boolean; terms: string[]; scored: boolean };
}

export interface PresenceGenerateResult {
  profile: PresenceProfileResult;
  /** The terms his work was mapped onto — "claim this, in their words." */
  bridges: PresenceBridgeItem[];
  /** Terms the engine refused to bridge (would overclaim) — surfaced, honest. */
  flags: string[];
  usedDistilled: boolean;
  /** Server-computed optimisation score for the chosen platform (additive;
   *  absent on older API builds). Render-only — never trust a client value. */
  optimisation?: PresenceOptimisation;
  /** R125: the render-blocking lint gate FAILED — the profile above is still served
   *  in full; the FE surfaces the reason instead of silently rendering past it. */
  blocked?: boolean;
  lintGate?: { location: string; kind: string; text: string };
  /** R125 (from P5's gatePresenceProse): about sentences carrying a hard claim that
   *  did not trace to the corpus — ADVISORY (the paragraph is not mutated). */
  aboutClaimFlags?: { sentence: string; missingSignal: string }[];
}

/** R125: warm the server's bridge cache before the user clicks Generate (fired on
 *  mount, fire-and-forget) so the generate call fits inside the edge's time budget. */
export function prewarmPresence(targetRoleFamily: string[]): Promise<{ warmed: boolean }> {
  return apiFetch<{ warmed: boolean }>('/presence/prewarm', {
    method: 'POST',
    body: JSON.stringify({ targetRoleFamily }),
  });
}

/** Generate platform profile copy, keyword-optimised for recruiter search,
 *  true at every claim level. Copy-paste output — we never post for the user.
 *  `platform` is the SERVED platform id (from getPlatforms) — typed wider than
 *  the legacy union so any lake platform passes, not just linkedin/seek/glassdoor. */
export async function generatePresence(
  platform: PresencePlatform | (string & {}),
  targetRoleFamily: string[],
) {
  return apiFetch<PresenceGenerateResult>('/presence/generate', {
    method: 'POST',
    body: JSON.stringify({ platform, targetRoleFamily }),
  });
}

// --- presence: saved versions (each generate-and-save is a NEW row) ----------

/** The request body for POST /presence/profiles — mirrors the server's
 *  SaveProfileBody. `profile` is the full generated PresenceProfile; the
 *  optimisation is NOT sent (the server recomputes + persists it — never trust a
 *  client score). */
export interface SaveProfileInput {
  platform: string;
  targetRoles: string[];
  englishVariant?: string;
  /** The full generated PresenceProfile (the /generate `profile` payload). */
  profile: PresenceProfileResult;
  /** The skill set (denormalised for query/filter; also inside `profile`). */
  skills: string[];
  /** Optional user label for this version. */
  label?: string;
}

/** One row in the saved-versions list (GET /presence/profiles → `profiles[]`).
 *  Lean by design — the headline `score` is surfaced off the stored optimisation;
 *  the full profile + readout load only on recall. */
export interface SavedProfileSummary {
  id: string;
  platform: string;
  label: string | null;
  targetRoles: string[];
  englishVariant: string | null;
  /** The headline optimisation score captured at save time, or null when the
   *  platform was unvalidated (never a fabricated number). */
  score: number | null;
  createdAt: string;
}

/** A recalled saved version in full (GET /presence/profiles/:id → `profile`).
 *  This is the stored `presence_profiles` row — the whole kit to re-render: the
 *  generated `profile` blocks plus the `optimisation` readout exactly as it was
 *  SCORED at save time (render the served blob; never recompute client-side). */
export interface SavedProfileFull {
  id: string;
  userId: string;
  platform: string;
  label: string | null;
  targetRoles: string[];
  englishVariant: string | null;
  profile: PresenceProfileResult;
  skills: string[];
  /** The PlatformOptimisation captured at save time, or null. */
  optimisation: PresenceOptimisation | null;
  createdAt: string;
  updatedAt: string;
}

/** Save the current generated profile as a NEW saved version (never an
 *  overwrite). The server recomputes + persists the optimisation, then returns
 *  the new row's id + createdAt. */
export async function saveSavedProfile(
  body: SaveProfileInput,
): Promise<{ saved: { id: string; createdAt: string } }> {
  return apiFetch<{ saved: { id: string; createdAt: string } }>('/presence/profiles', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** List the user's saved versions, newest first (lean rows for the drawer). */
export async function listSavedProfiles(): Promise<{ profiles: SavedProfileSummary[] }> {
  return apiFetch<{ profiles: SavedProfileSummary[] }>('/presence/profiles');
}

/** Recall one saved version in full — the whole kit to re-render the page. */
export async function recallSavedProfile(id: string): Promise<{ profile: SavedProfileFull }> {
  return apiFetch<{ profile: SavedProfileFull }>(`/presence/profiles/${encodeURIComponent(id)}`);
}

// --- presence: platforms (SERVED from the lake) + title feedback ledger ---

/** One profile-buildable platform the lake knows about, with how many rules
 *  back it. Served — the FE holds NO hardcoded platform list. */
export interface PresencePlatformProfile {
  platform: string;
  ruleCount: number;
  /** The 3-way breakdown of this platform's validated criteria (decision-7): the chip
   *  badge shows `scored` (what actually drives the %); a tap reveals all three. Absent
   *  when the criteria lake read degrades. */
  criteria?: { scored: number; guaranteed: number; advisory: number };
}

/** The lake's view of presence platforms: profile-buildable surfaces, the ATS
 *  parsers (optimised in the Application workflow, not here), and the count of
 *  cross-platform universal rules. Shape mirrors GET /api/presence/platforms. */
export interface PresencePlatformsResponse {
  profiles: PresencePlatformProfile[];
  atsParsers: PresencePlatformProfile[];
  universals: number;
}

/** The lake's platform catalogue — profile-buildable platforms + ATS parsers +
 *  the universal-rule count. Drives the platform chips (no hardcoded array). */
export async function getPlatforms(): Promise<PresencePlatformsResponse> {
  return apiFetch<PresencePlatformsResponse>('/presence/platforms');
}

/** Which surface a title-feedback signal came from. */
export type TitleFeedbackSurface = 'presence' | 'role_match';
/** What the user did with a recommended title: kept it, dismissed it, or applied. */
export type TitleFeedbackAction = 'chose' | 'dismissed' | 'applied';

export interface TitleFeedbackInput {
  surface: TitleFeedbackSurface;
  /** The recommended-title slot index, when the signal is positional. */
  slot?: number;
  title: string;
  action: TitleFeedbackAction;
}

/** Record a title-feedback signal (chose / dismissed / applied) into the
 *  per-user PresenceRefineDO ledger, so future re-ranks learn from real choices.
 *  Fire-and-forget at the call site; the server returns {ok:true}. */
export async function sendTitleFeedback(input: TitleFeedbackInput): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/presence/title-feedback', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// --- corpus readiness: the graduation gauge ("am I done feeding it?") ---

export interface CorpusReadiness {
  /** 0..100, deterministic — derived from corpus state, never model self-report. */
  score: number;
  readyToTailor: boolean;
  parts: {
    coverage: number;
    evidence: number;
    gold: number;
    throughLine: boolean;
    distilled: boolean;
  };
  /** Ranked, human-readable "what's still missing" lines. */
  missing: string[];
}

export async function getCorpusReadiness() {
  return apiFetch<CorpusReadiness>('/corpus/readiness');
}

// --- distilled profile: auto re-derive (the true CV keeps itself current) ---

let rederiveTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounced, fire-and-forget: after a corpus mutation (combine-refine, reframe,
 *  unmerge, Fix-all, import) the distilled true CV re-derives itself so it always
 *  reflects the corpus. Failures are swallowed — this is background upkeep; the
 *  user never waits on it and the next mutation simply tries again. */
export function scheduleDistilledRederive(delayMs = 2500) {
  if (rederiveTimer) clearTimeout(rederiveTimer);
  rederiveTimer = setTimeout(() => {
    rederiveTimer = null;
    apiFetch('/distilled-profile/derive', { method: 'POST' }).catch(() => {});
  }, delayMs);
}

export interface CoverLetterImportInput {
  text: string;
  targetCompany?: string;
  targetRole?: string;
  outcome?: 'pending' | 'no_callback' | 'callback' | 'screen' | 'onsite' | 'offer';
  sentOn?: string;
  documentDate?: string;
}

export interface PasteImportInput {
  source: 'linkedin' | 'cv' | 'paste';
  text: string;
  /** YYYY-MM-DD; "Present" in an old CV is anchored to this date, not today. */
  documentDate?: string;
}

// --- discovery + watchlist types --------------------------------------------

export interface DiscoveryAccount {
  id: string;
  email: string;
  displayName: string | null;
  picture: string | null;
  enabled: boolean;
  lastPolledAt: string | null;
  lastJobsFound: number | null;
  lastError: string | null;
}

export type WatchlistKind =
  | 'seek_rss'
  | 'greenhouse_board'
  | 'lever_board'
  | 'rss_generic'
  | 'discovery_email';

export interface WatchlistSource {
  id: string;
  userId: string;
  kind: WatchlistKind;
  label: string;
  /**
   * Per-kind shape:
   *  - seek_rss / rss_generic:  { url }
   *  - greenhouse_board:        { boardSlug, filterTerms?: string[], locationContains?: string }
   *  - lever_board:             { boardSlug, filterTerms?: string[], locationContains?: string }
   *  - discovery_email:         { discoveryAccountId }
   */
  config: Record<string, unknown>;
  enabled: boolean;
  lastPolledAt: string | null;
  lastJobsFound: number | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CronRunRow {
  id: string;
  jobName: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'ok' | 'partial' | 'error' | string;
  metricsJson: {
    sources?: number;
    candidates?: number;
    ingested?: number;
    scored?: number;
    errorCount?: number;
    perSource?: Record<string, { rawCount: number; candidates: number; error: string | null }>;
  } | null;
  errors: { url?: string; message: string }[] | null;
}

export interface PollResultBody {
  ok: boolean;
  sourcesPolled: number;
  candidates: number;
  ingestedNew: number;
  scored: number;
  perSource: Record<string, { rawCount: number; candidates: number; error: string | null }>;
  errors: { url: string; message: string }[];
}

// ── Applications (P1 — the apply terminus) ───────────────────────────────────
// Column order + labels. Typed by the schema's ApplicationStatus so the FE can only
// ever hold valid states (drift-guard with the P0 union + the DB CHECK + the schema).
export const APPLICATION_STATUS_ORDER: ApplicationStatus[] = [
  'wishlist',
  'applied',
  'screen',
  'onsite',
  'offer',
  'closed',
];

export const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  wishlist: 'Wishlist',
  applied: 'Applied',
  screen: 'Screen',
  onsite: 'Onsite',
  offer: 'Offer',
  closed: 'Closed',
};

export interface Application {
  id: string;
  jobId: string;
  resumeId: string | null;
  coverLetterId: string | null;
  status: ApplicationStatus;
  appliedAt: string | null;
  closedAt: string | null;
  notes: string | null;
  /** Joined from the job on GET /applications (leftJoin — null if the job was removed). */
  jobTitle?: string | null;
  jobCompany?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationEvent {
  id: string;
  applicationId: string;
  eventType: 'created' | 'status_changed';
  occurredAt: string;
  summary: string | null;
  metadata: Record<string, unknown> | null;
}

export async function listApplications(): Promise<Application[]> {
  const r = await apiFetch<{ applications: Application[] }>('/applications');
  return r.applications;
}

export async function trackApplication(input: {
  jobId: string;
  resumeId?: string;
  coverLetterId?: string;
  status?: ApplicationStatus;
  notes?: string;
}): Promise<{ application: Application; created: boolean }> {
  return apiFetch('/applications', { method: 'POST', body: JSON.stringify(input) });
}

export async function transitionApplication(
  id: string,
  status: ApplicationStatus,
  notes?: string,
): Promise<{ application: Application }> {
  return apiFetch(`/applications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, ...(notes !== undefined ? { notes } : {}) }),
  });
}

export async function getApplication(
  id: string,
): Promise<{ application: Application; events: ApplicationEvent[] }> {
  return apiFetch(`/applications/${id}`);
}
